import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { ERAS, BLEND_WIDTH } from '../config/eras'
import { resolveEraBlend } from '../core/time/timeEngine'
import { useExperienceStore } from '../store/experienceStore'

const loader = new THREE.TextureLoader()
const textureCache = new Map<string, THREE.Texture>()

function loadEraTexture(url: string): Promise<THREE.Texture | null> {
  if (textureCache.has(url)) return Promise.resolve(textureCache.get(url)!)
  return new Promise((resolve) => {
    loader.load(
      url,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace
        tex.mapping = THREE.EquirectangularReflectionMapping
        tex.minFilter = THREE.LinearMipmapLinearFilter
        tex.magFilter = THREE.LinearFilter
        tex.generateMipmaps = true
        textureCache.set(url, tex)
        resolve(tex)
      },
      undefined,
      () => resolve(null),
    )
  })
}

/**
 * Cinematic dual-era sky:
 * - Primary: THREE equirectangular scene.background (engine-correct projection)
 * - Crossfade: full-screen BackSide sphere shader sampling both eras
 */
export function EnvironmentMorph() {
  const markEraLoaded = useExperienceStore((s) => s.markEraLoaded)
  const { gl, scene } = useThree()
  const matRef = useRef<THREE.ShaderMaterial>(null)
  const meshRef = useRef<THREE.Mesh>(null)
  const lastBg = useRef<string>('')

  const uniforms = useMemo(
    () => ({
      uTexA: { value: null as THREE.Texture | null },
      uTexB: { value: null as THREE.Texture | null },
      uBlend: { value: 0 },
      uColorA: { value: new THREE.Color('#102030') },
      uColorB: { value: new THREE.Color('#102030') },
      uHasA: { value: 0 },
      uHasB: { value: 0 },
      uTime: { value: 0 },
      uSpeed: { value: 0 },
      uIntensity: { value: 0 },
    }),
    [],
  )

  useEffect(() => {
    gl.toneMapping = THREE.ACESFilmicToneMapping
    gl.toneMappingExposure = 1.0
  }, [gl])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      await Promise.all(
        ERAS.map(async (era) => {
          const url = `/assets/eras/${era.folder}/env.png`
          const tex = await loadEraTexture(url)
          if (cancelled || !tex) return
          const maxA = gl.capabilities.getMaxAnisotropy?.() ?? 1
          tex.anisotropy = Math.min(8, maxA)
          markEraLoaded(era.id)
        }),
      )
    })()
    return () => {
      cancelled = true
    }
  }, [markEraLoaded, gl])

  useFrame((state, dt) => {
    const { timeValue, traverseIntensity, timeSpeed, tick } =
      useExperienceStore.getState()
    tick(dt)

    const blend = resolveEraBlend(timeValue, ERAS, BLEND_WIDTH)
    const t = blend.from.id === blend.to.id ? 0 : blend.t
    const urlA = `/assets/eras/${blend.from.folder}/env.png`
    const urlB = `/assets/eras/${blend.to.folder}/env.png`
    const texA = textureCache.get(urlA) ?? null
    const texB = textureCache.get(urlB) ?? null

    // Engine-native equirect background for correct, sharp environment
    if (texA && lastBg.current !== urlA) {
      scene.background = texA
      lastBg.current = urlA
    } else if (!texA) {
      scene.background = new THREE.Color(blend.from.colors[0])
      lastBg.current = ''
    }

    // Sphere handles blend + motion energy (visible when morphing / moving)
    uniforms.uTexA.value = texA
    uniforms.uTexB.value = texB
    uniforms.uHasA.value = texA ? 1 : 0
    uniforms.uHasB.value = texB ? 1 : 0
    uniforms.uBlend.value = t
    uniforms.uColorA.value.set(blend.from.colors[1])
    uniforms.uColorB.value.set(blend.to.colors[1])
    uniforms.uTime.value = state.clock.elapsedTime
    uniforms.uSpeed.value = timeSpeed
    uniforms.uIntensity.value = traverseIntensity
    if (matRef.current) {
      // show sphere when blending or time is flowing for warp
      const show = t > 0.01 || Math.abs(timeSpeed) > 0.02 || traverseIntensity > 0.05
      matRef.current.visible = show
      matRef.current.opacity = show ? 1 : 0
      matRef.current.uniformsNeedUpdate = true
    }

    if (meshRef.current) {
      meshRef.current.rotation.y +=
        dt * (0.003 + Math.abs(timeSpeed) * 0.12 + traverseIntensity * 0.03)
    }
  })

  return (
    <mesh ref={meshRef} scale={[-1, 1, 1]} frustumCulled={false}>
      <sphereGeometry args={[160, 96, 64]} />
      <shaderMaterial
        ref={matRef}
        transparent
        depthWrite={false}
        side={THREE.BackSide}
        toneMapped
        vertexShader={/* glsl */ `
          varying vec3 vWorldPos;
          void main() {
            vec4 w = modelMatrix * vec4(position, 1.0);
            vWorldPos = w.xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
        fragmentShader={/* glsl */ `
          precision highp float;
          uniform sampler2D uTexA;
          uniform sampler2D uTexB;
          uniform float uBlend;
          uniform vec3 uColorA;
          uniform vec3 uColorB;
          uniform float uHasA;
          uniform float uHasB;
          uniform float uTime;
          uniform float uSpeed;
          uniform float uIntensity;
          varying vec3 vWorldPos;
          const float PI = 3.14159265359;
          vec2 equirectUV(vec3 dir) {
            dir = normalize(dir);
            float u = atan(dir.z, dir.x) / (2.0 * PI) + 0.5;
            float v = asin(clamp(dir.y, -1.0, 1.0)) / PI + 0.5;
            return vec2(u, v);
          }
          void main() {
            vec3 dir = normalize(vWorldPos);
            float warp = abs(uSpeed) * 0.02 + uIntensity * 0.03;
            vec2 uv = equirectUV(dir);
            uv.x += sin(uv.y * 10.0 + uTime * 0.5) * warp;
            vec3 a = uHasA > 0.5 ? texture2D(uTexA, uv).rgb : uColorA;
            vec3 b = uHasB > 0.5 ? texture2D(uTexB, uv).rgb : uColorB;
            float t = smoothstep(0.0, 1.0, clamp(uBlend, 0.0, 1.0));
            // when not blending, still show subtle warp of current era
            if (t < 0.001) {
              gl_FragColor = vec4(a, 0.35 + abs(uSpeed) * 0.4 + uIntensity * 0.3);
            } else {
              vec3 col = mix(a, b, t);
              gl_FragColor = vec4(col, 0.55 + t * 0.4);
            }
          }
        `}
        uniforms={uniforms}
      />
    </mesh>
  )
}
